import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, AutoAwesome, ExpandMore } from "@mui/icons-material";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSurveyAnalytics, useSurveyDetails, useSurveyResponses } from "../../hooks/useSurveys";
import { getDashboardMetricInsights } from "../../api/client";
import MetricAiInsightsDialog from "../../components/MetricAiInsightsDialog";

const formatPercent = (value) => (value == null ? "—" : `${Number(value).toFixed(1)}%`);
const formatMinutes = (seconds) =>
  seconds == null ? "—" : `${Math.max(1, Math.round(Number(seconds) / 60))} min`;
const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
};

const toMs = (value) => {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const getSessionDurationSeconds = (session) => {
  const startedMs = toMs(session?.startedAt);
  const completedMs = toMs(session?.completedAt);
  if (startedMs != null && completedMs != null && completedMs >= startedMs) {
    return Math.max(0, Math.round((completedMs - startedMs) / 1000));
  }
  const fallback = session?.timeToCompleteSeconds ?? session?.timeToComplete ?? null;
  const n = fallback == null ? null : Number(fallback);
  return n == null || Number.isNaN(n) ? null : n;
};

const getMetricTypeLabel = (metricType) => {
  const key = String(metricType || "").toLowerCase();
  if (key === "nps") return "NPS";
  if (key === "csat") return "CSAT";
  if (key === "ces") return "CES";
  return "Score";
};

const getPrimaryObjectiveText = (metricType) => {
  const key = String(metricType || "").toLowerCase();
  if (key === "nps") return "Objetivo: entender lealdade e recomendação dos clientes.";
  if (key === "csat") return "Objetivo: medir satisfação com atendimento/serviço.";
  if (key === "ces") return "Objetivo: medir esforço do cliente para resolver uma necessidade.";
  return "Objetivo: acompanhar a percepção geral dos respondentes.";
};

const toNumericLabel = (option) => {
  const candidates = [option?.value, option?.label, option?.averageScore];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const value = Number(candidate);
    if (!Number.isNaN(value)) return String(value);
  }
  return null;
};

const getNpsBand = (value) => {
  if (value == null) return { label: "Sem dados suficientes", color: "default" };
  if (value < 0) return { label: "Zona crítica", color: "error" };
  if (value < 30) return { label: "Zona de atenção", color: "warning" };
  if (value < 60) return { label: "Zona de melhoria", color: "info" };
  return { label: "Zona de excelência", color: "success" };
};

const StatCard = ({ label, value, helper }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      borderRadius: 2,
      borderColor: "divider",
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
      bgcolor: "background.paper",
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, lineHeight: 1.2, fontWeight: 700 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

export default function SurveyAnalyticsPage() {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const surveyId = Number(id);
  const { data: survey, isLoading: loadingSurvey, error: surveyError } = useSurveyDetails(surveyId, true);
  const { data: analytics, isLoading: loadingAnalytics, error: analyticsError } = useSurveyAnalytics(
    surveyId,
    true
  );
  const { data: responsesData } = useSurveyResponses(surveyId, 1, 50, "", true);
  const [selectedQuestionId, setSelectedQuestionId] = useState("");
  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = useState("");
  const [metricDialogName, setMetricDialogName] = useState("");
  const [metricDialogLabel, setMetricDialogLabel] = useState("");
  const [metricDialogHuman, setMetricDialogHuman] = useState("");
  const [metricDialogData, setMetricDialogData] = useState(null);
  const [metricDialogError, setMetricDialogError] = useState(null);
  const [metricDialogBasePayload, setMetricDialogBasePayload] = useState(null);

  const questions = analytics?.questions || [];
  const selectedQuestion =
    questions.find((question) => String(question.questionId) === String(selectedQuestionId)) ||
    questions[0] ||
    null;

  const chartData = useMemo(
    () =>
      (selectedQuestion?.options || []).map((option) => ({
        name: (() => {
          const isNumericScale =
            selectedQuestion?.type === "nps" ||
            selectedQuestion?.type === "rating_scale" ||
            selectedQuestion?.type === "emoji_scale";
          if (!isNumericScale) return option.label || option.value || "Opção";
          const numericLabel = toNumericLabel(option);
          return `Nota ${numericLabel || "?"}`;
        })(),
        respostas: Number(option.count || 0),
      })),
    [selectedQuestion]
  );

  const executive = analytics?.executive || {};
  const scoreSummary = executive.scoreSummary || {};
  const metricType = scoreSummary.metricType || survey?.metricType || "custom";
  const metricTypeLabel = getMetricTypeLabel(metricType);
  const npsBand = getNpsBand(scoreSummary.mainValue);
  const trend = analytics?.trend || [];
  const hasTrend = trend.some(
    (row) => Number(row.startedCount || 0) > 0 || Number(row.completedCount || 0) > 0
  );
  const totalOpenTexts = (analytics?.openTextResponses || []).length;
  const selectedQuestionOpenTexts = useMemo(() => {
    if (!selectedQuestion?.questionId) return [];
    return (analytics?.openTextResponses || []).filter(
      (item) => String(item.questionId) === String(selectedQuestion.questionId)
    );
  }, [analytics?.openTextResponses, selectedQuestion?.questionId]);
  const completionRate = executive.completionRate ?? null;
  const startedCount = Number(executive.startedCount || 0);
  const completedCount = Number(executive.completedCount || 0);
  const dropoffCount = Math.max(0, startedCount - completedCount);
  const sessions = analytics?.sessions || [];
  const computedAvgCompletionSeconds = useMemo(() => {
    const completedSessions = (sessions || []).filter((s) => String(s?.status || "") === "completed");
    const durations = completedSessions
      .map((s) => getSessionDurationSeconds(s))
      .filter((n) => typeof n === "number" && Number.isFinite(n) && n >= 0);
    if (!durations.length) return null;
    return Math.round(durations.reduce((sum, n) => sum + n, 0) / durations.length);
  }, [sessions]);
  const answersBySession = useMemo(() => {
    const map = new Map();
    for (const session of responsesData?.responses || []) {
      map.set(session.sessionId, session.answers || []);
    }
    return map;
  }, [responsesData]);

  const topOption = useMemo(() => {
    if (!chartData.length) return null;
    return chartData.slice().sort((a, b) => b.respostas - a.respostas)[0];
  }, [chartData]);
  const formatAnswer = (answer) => {
    if (!answer) return "Sem resposta";
    if (answer.optionLabel) return answer.optionLabel;
    if (answer.optionValue) return String(answer.optionValue);
    if (answer.answerNumber != null) return `Nota ${answer.answerNumber}`;
    if (answer.answerText) return answer.answerText;
    if (answer.answerScore != null) return `Score ${answer.answerScore}`;
    return "Sem resposta";
  };

  const isLoading = loadingSurvey || loadingAnalytics;
  const errorMessage = surveyError?.message || analyticsError?.message;

  const metricInsightsMutation = useMutation(
    async (cfg) =>
      getDashboardMetricInsights("surveys", {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricLabel: cfg.metricLabel,
        metricDefinition: cfg.metricDefinition,
        metricValue: cfg.metricValue,
        metricHuman: cfg.metricHuman,
        period: {
          start: survey?.createdAt || survey?.data_cadastro || null,
          end: new Date().toISOString().slice(0, 10),
        },
        summaryContext: {
          surveyId,
          surveyTitle: survey?.title || survey?.titulo || "",
          scoreSummary,
          executive,
        },
      }),
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        setMetricDialogError(err?.message || "Não foi possível gerar a explicação da IA.");
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric || "");
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg || null);
    setMetricDialogData(null);
    setMetricDialogError(null);
    setMetricDialogOpen(true);
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg, title = "Explicar com IA" }) => (
    <MuiTooltip title={title}>
      <IconButton size="small" onClick={() => openAi(cfg)} aria-label={title}>
        <AutoAwesome fontSize="inherit" />
      </IconButton>
    </MuiTooltip>
  );

  const sectionCardSx = {
    borderRadius: 2,
    borderColor: "divider",
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    bgcolor: "background.paper",
  };

  const sectionTitleSx = {
    fontWeight: 700,
    letterSpacing: "-0.01em",
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent sx={{ p: 2.25 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                  Resultado da pesquisa
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {survey?.title || survey?.titulo || "Pesquisa"}
                </Typography>
              </Box>
              <Button startIcon={<ArrowBack />} onClick={() => navigate("/surveys")} variant="outlined">
                Voltar para pesquisas
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {isLoading ? <Alert severity="info">Carregando dados de analytics...</Alert> : null}

        {!isLoading && analytics ? (
          <Stack spacing={2.5}>
            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                      Leitura principal
                    </Typography>
                    <AiBtn
                      cfg={{
                        metric: "survey_resultado_principal",
                        metricName: "Resultado principal da pesquisa",
                        metricLabel: metricTypeLabel,
                        metricDefinition:
                          "Interpretação da métrica principal da pesquisa considerando objetivo e contexto.",
                        metricValue: {
                          scoreSummary,
                          completionRate,
                          startedCount,
                          completedCount,
                        },
                        metricHuman: String(scoreSummary.mainValue ?? "—"),
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {getPrimaryObjectiveText(metricType)}
                  </Typography>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ md: "center" }}
                    sx={{ p: 1.5, borderRadius: 2, bgcolor: theme.palette.action.hover }}
                  >
                    <Box sx={{ minWidth: 190 }}>
                      <Typography variant="caption" color="text.secondary">
                        Indicador principal ({metricTypeLabel})
                      </Typography>
                      <Typography variant="h2" sx={{ lineHeight: 1.1 }}>
                        {scoreSummary.mainValue ?? "—"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Amostra: ${scoreSummary.sampleSize ?? 0}`} variant="outlined" />
                      <Chip
                        label={`Favorabilidade: ${formatPercent(scoreSummary.favorability)}`}
                        variant="outlined"
                      />
                        {metricTypeLabel === "NPS" ? (
                        <Chip label={npsBand.label} color={npsBand.color} sx={{ fontWeight: 600 }} />
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <StatCard label="Sessões iniciadas" value={startedCount} helper="Pessoas que começaram a responder" />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  label="Sessões concluídas"
                  value={completedCount}
                  helper="Pessoas que enviaram respostas"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard label="Taxa de conclusão" value={formatPercent(completionRate)} helper="Concluídas / iniciadas" />
              </Grid>
              <Grid item xs={12} md={3}>
                <StatCard
                  label="Tempo médio de resposta"
                  value={formatMinutes(computedAvgCompletionSeconds ?? executive.averageCompletionSeconds)}
                  helper="Tempo até concluir"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={8}>
                <Card variant="outlined" sx={{ height: "100%", ...sectionCardSx }}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Evolução diária
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_tendencia",
                          metricName: "Evolução diária de respostas",
                          metricLabel: "Tendência",
                          metricDefinition:
                            "Evolução diária de sessões iniciadas e concluídas para medir engajamento.",
                          metricValue: trend,
                          metricHuman: "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Veja se as pessoas estão respondendo e concluindo com consistência ao longo dos dias.
                    </Typography>
                    <Box sx={{ width: "100%", height: 320 }}>
                      {hasTrend ? (
                        <ResponsiveContainer>
                          <LineChart data={trend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip />
                            <Legend />
                            <Line type="monotone" dataKey="startedCount" stroke="#1976d2" name="Iniciadas" />
                            <Line type="monotone" dataKey="completedCount" stroke="#2e7d32" name="Concluídas" />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Ainda não há volume suficiente para exibir tendência.
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Card variant="outlined" sx={{ height: "100%", ...sectionCardSx }}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Typography variant="h6" sx={sectionTitleSx}>
                      Funil de participação
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Mostra quantas pessoas começaram e quantas realmente terminaram.
                    </Typography>
                    <Stack spacing={1.25}>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Iniciaram
                        </Typography>
                        <Typography variant="h5">{startedCount}</Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Concluíram
                        </Typography>
                        <Typography variant="h5">{completedCount}</Typography>
                      </Box>
                      <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                        <Typography variant="caption" color="text.secondary">
                          Não concluíram
                        </Typography>
                        <Typography variant="h5">{dropoffCount}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Resultado por pergunta
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_resultado_pergunta",
                          metricName: "Distribuição por pergunta",
                          metricLabel: "Pergunta",
                          metricDefinition:
                            "Distribuição de respostas da pergunta selecionada para identificar padrão dominante.",
                          metricValue: {
                            question: selectedQuestion?.title || "",
                            type: selectedQuestion?.type || "",
                            chartData,
                          },
                          metricHuman: selectedQuestion?.title || "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Selecione uma pergunta para ver exatamente quais respostas foram escolhidas.
                    </Typography>
                  </Box>
                  <FormControl sx={{ minWidth: { xs: "100%", md: 320 } }}>
                    <InputLabel>Pergunta</InputLabel>
                    <Select
                      label="Pergunta"
                      value={selectedQuestion?.questionId || ""}
                      onChange={(event) => setSelectedQuestionId(event.target.value)}
                    >
                      {questions.map((question) => (
                        <MenuItem key={question.questionId} value={question.questionId}>
                          {question.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {selectedQuestion ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={8}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip label={`Tipo: ${selectedQuestion.type}`} variant="outlined" />
                        {selectedQuestion.scaleMax ? (
                          <Chip
                            label={`Escala ${selectedQuestion.scaleMin}-${selectedQuestion.scaleMax}`}
                            variant="outlined"
                          />
                        ) : null}
                      </Stack>
                      <Box sx={{ width: "100%", height: 280 }}>
                        {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text" ? (
                          selectedQuestionOpenTexts.length ? (
                            <Stack spacing={1} sx={{ height: "100%", overflowY: "auto", pr: 1 }}>
                              {selectedQuestionOpenTexts.slice(0, 50).map((item, index) => (
                                <Card key={`open-answer-${index}`} variant="outlined">
                                  <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                                    <Typography variant="body2">{item.answerText}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDateTime(item.answeredAt)}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          ) : (
                            <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center">
                              <Typography variant="body2" color="text.secondary">
                                Esta pergunta aberta ainda não recebeu respostas.
                              </Typography>
                            </Stack>
                          )
                        ) : chartData.length ? (
                          <ResponsiveContainer>
                            <BarChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis allowDecimals={false} />
                              <RechartsTooltip />
                              <Legend />
                              <Bar dataKey="respostas" fill="#1976d2" name="Respostas" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center">
                            <Typography variant="body2" color="text.secondary">
                              Esta pergunta ainda não recebeu respostas.
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Leitura rápida da pergunta
                          </Typography>
                          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Pergunta
                              </Typography>
                              <Typography variant="body2">
                                {selectedQuestion.title || "Pergunta sem título"}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text"
                                  ? "Respostas de texto"
                                  : "Opção mais escolhida"}
                              </Typography>
                              <Typography variant="body2">
                                {selectedQuestion?.type === "short_text" || selectedQuestion?.type === "long_text"
                                  ? `${selectedQuestionOpenTexts.length} comentário(s) nesta pergunta`
                                  : topOption
                                  ? `${topOption.name} (${topOption.respostas} respostas)`
                                  : "Sem dados suficientes"}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary">
                    Ainda não há perguntas com dados para exibir.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Temas mais citados
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_temas",
                          metricName: "Temas mais citados",
                          metricLabel: "Temas",
                          metricDefinition: "Principais assuntos detectados em respostas abertas.",
                          metricValue: analytics.textInsights?.topThemes || [],
                          metricHuman: "",
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Palavras e temas que mais aparecem nos comentários.
                    </Typography>
                    <Stack spacing={1}>
                      {(analytics.textInsights?.topThemes || []).length ? (
                        analytics.textInsights.topThemes.map((theme) => (
                          <Stack key={theme.label} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{theme.label}</Typography>
                            <Chip label={theme.count} size="small" />
                          </Stack>
                        ))
                      ) : (
                        <Typography color="text.secondary">
                          Ainda não há temas suficientes nas respostas abertas.
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={sectionCardSx}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                        Comentários recentes
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "survey_comentarios",
                          metricName: "Comentários recentes",
                          metricLabel: "Verbatims",
                          metricDefinition:
                            "Comentários textuais recentes para identificar elogios, dores e oportunidades.",
                          metricValue: (analytics.openTextResponses || []).slice(0, 20),
                          metricHuman: `${totalOpenTexts} comentários`,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Últimos comentários recebidos ({totalOpenTexts} no total), em ordem recente.
                    </Typography>
                    <Stack spacing={1} sx={{ maxHeight: 260, overflowY: "auto" }}>
                      {(analytics.openTextResponses || []).length ? (
                        analytics.openTextResponses.slice(0, 8).map((item, index) => (
                          <Box
                            key={`${item.answeredAt}-${index}`}
                            sx={{ pb: 1, px: 1.25, py: 1, borderRadius: 1.5, bgcolor: "action.hover" }}
                          >
                            <Typography variant="subtitle2">{item.questionTitle}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.answerText}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary">Sem respostas abertas registradas.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined" sx={sectionCardSx}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ flex: 1, ...sectionTitleSx }}>
                    Sessões recentes
                  </Typography>
                  <AiBtn
                    cfg={{
                      metric: "survey_sessoes",
                      metricName: "Sessões recentes",
                      metricLabel: "Sessões",
                      metricDefinition:
                        "Amostra das sessões mais recentes da pesquisa com status, canal e tempo.",
                      metricValue: sessions.slice(0, 20),
                      metricHuman: `${sessions.length} sessões`,
                    }}
                  />
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Token</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Canal</TableCell>
                      <TableCell>Início</TableCell>
                      <TableCell>Conclusão</TableCell>
                      <TableCell>Tempo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.slice(0, 10).map((session) => (
                      <TableRow
                        key={session.id}
                        sx={{
                          "&:nth-of-type(even)": { bgcolor: "action.hover" },
                        }}
                      >
                        <TableCell>{session.responseToken}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={session.status}
                            color={session.status === "completed" ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{session.sourceChannel || "—"}</TableCell>
                        <TableCell>{formatDateTime(session.startedAt)}</TableCell>
                        <TableCell>{formatDateTime(session.completedAt)}</TableCell>
                        <TableCell>{formatMinutes(getSessionDurationSeconds(session))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  O que cada pessoa respondeu
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Abra uma sessão para ver pergunta por pergunta.
                </Typography>
                <Stack spacing={1}>
                  {sessions.slice(0, 20).map((session) => {
                    const answers = answersBySession.get(session.id) || [];
                    return (
                      <Accordion
                        key={`session-${session.id}`}
                        disableGutters
                        elevation={0}
                        variant="outlined"
                        sx={{ borderRadius: 1.5, overflow: "hidden", "&:before": { display: "none" } }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={{ xs: 0.5, md: 2 }}
                            alignItems={{ md: "center" }}
                            sx={{ width: "100%" }}
                          >
                            <Typography variant="body2" sx={{ minWidth: 220 }}>
                              {session.responseToken}
                            </Typography>
                            <Chip
                              size="small"
                              label={session.status}
                              color={session.status === "completed" ? "success" : "default"}
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {answers.length} resposta(s) registrada(s)
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          {answers.length ? (
                            <Stack spacing={1}>
                              {answers.map((answer, index) => (
                                <Box key={`ans-${session.id}-${index}`}>
                                  <Typography variant="caption" color="text.secondary">
                                    {answer.questionTitle || "Pergunta"}
                                  </Typography>
                                  <Typography variant="body2">{formatAnswer(answer)}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Esta sessão ainda não possui respostas detalhadas disponíveis.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </Stack>
      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={metricDialogBasePayload?.period || null}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="surveys"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
    </Container>
  );
}
